import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import type { EventFormData } from './event-form.schema'
import type { CreateEventPayload } from '../data/api/admin-events.api-datasource'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Convert a date-only string (YYYY-MM-DD) to UTC midnight ISO.
 * Preserves the calendar date regardless of timezone.
 */
const dateToUtc = (date: string): string => `${date.split('T')[0]}T00:00:00.000Z`

/**
 * Convert a datetime-local value to UTC ISO using the event's timezone.
 * "2024-03-15T14:30" + "America/New_York" → "2024-03-15T18:30:00.000Z"
 */
const datetimeToUtc = (datetime: string, tz: string): string => {
  if (!datetime) return datetime
  return dayjs.tz(datetime, tz).utc().toISOString()
}

/** Conditionally convert a datetime string — returns undefined if empty. */
const optDatetime = (value: string | undefined, tz: string): string | undefined =>
  value ? datetimeToUtc(value, tz) : undefined

/**
 * Transform validated form data into the backend API payload shape.
 * All datetime fields are converted from the event's timezone to UTC.
 */
export const formDataToPayload = (data: EventFormData, eventCategoryId: number): CreateEventPayload => {
  const tz = data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone

  return {
    title: data.eventName,
    startDate: dateToUtc(data.eventDate),
    endDate: data.displayUntil ? datetimeToUtc(data.displayUntil, tz) : dateToUtc(data.eventDate),
    venueName: data.venueName ?? '',
    address: {
      addressLine1: data.addressLine1 ?? '',
      addressLine2: data.addressLine2,
      city: data.city,
      state: data.state ?? '',
      country: data.country,
      zipCode: data.zipCode ?? ''
    },
    eventCategoryId,
    timezone: data.timezone || undefined,
    isPinned: data.isPinned || undefined,
    publishAt: optDatetime(data.publishAt, tz),

    // Early venue access — explicit boolean so backend column flips on toggle.
    // (db-to-model only emits features.earlyAccess when column is strictly false,
    // so undefined leaves the column NULL and load-back returns nothing.)
    disabledEarlyAccess: !data.earlyAccessEnabled,
    rsvpButtonText: data.earlyAccessEnabled ? data.rsvpButtonText : undefined,
    rsvpStart: data.earlyAccessEnabled ? optDatetime(data.rsvpStart, tz) : undefined,
    rsvpEnd: data.earlyAccessEnabled ? optDatetime(data.rsvpEnd, tz) : undefined,
    rsvpInfo: data.earlyAccessEnabled ? data.rsvpInfo : undefined,
    rsvpLeaveInfo: data.earlyAccessEnabled ? data.rsvpLeaveInfo : undefined,
    rsvpCapacity: data.earlyAccessEnabled && data.rsvpCapacity ? Number(data.rsvpCapacity) : undefined,

    // Ticket sales — only include if section enabled
    presaleUrl: data.ticketSalesEnabled ? data.ticketUrl || undefined : undefined,
    linkButtonText: data.ticketSalesEnabled ? data.ticketButtonText || undefined : undefined,

    // Presale — only include if section enabled
    presaleCode: data.presaleEnabled ? data.presaleCode : undefined,
    presaleStart: data.presaleEnabled ? optDatetime(data.presaleStart, tz) : undefined,
    presaleEnd: data.presaleEnabled ? optDatetime(data.presaleEnd, tz) : undefined,

    // VIP — only include if section enabled
    vipUrl: data.vipEnabled ? data.vipUrl : undefined,
    vipButtonText: data.vipEnabled ? data.vipButtonText : undefined,

    // Band add-ons — only include if section enabled
    fanMeetAndGreetStart: data.addonsEnabled ? optDatetime(data.meetAndGreetStart, tz) : undefined,
    fanMeetAndGreetEnd: data.addonsEnabled ? optDatetime(data.meetAndGreetEnd, tz) : undefined,
    fanMeetAndGreetDialogJoinText: data.addonsEnabled ? data.meetAndGreetJoinText : undefined,
    fanMeetAndGreetDialogLeaveText: data.addonsEnabled ? data.meetAndGreetLeaveText : undefined,
    meetButtonText: data.addonsEnabled ? data.meetButtonText : undefined
  }
}
